
import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase/client';
import AuthContext from '../context/AuthContext';
import { AuthUser } from '../types/AuthTypes';
import { checkRoleAccess } from '../utils/roleUtils';
import { roleService } from '@/services/auth/role/roleService';
import { getProfile } from '@/services/auth/profileService';
import { debugLog, debugError } from '@/utils/debugLogger';
import { safeLocalStorage } from '@/utils/safeStorage';
import { useChatStore } from '@/store/useChatStore';
import { checkUserConsent, saveUserConsent } from '@/services/consent/userConsentService';
import { checkDealerTermsConsent, saveDealerTermsConsent } from '@/services/consent/dealerTermsConsentService';
import { savePostConfirmationConsents } from '@/services/auth/postConfirmationConsent';
import { waitForHydrationComplete } from '@/utils/hydrationManager';
import { checkSignupEmail } from '@/lib/auth/emailGuard';
import { getEmailGuardOptionsCached } from '@/lib/auth/signupProtection';
import { logSignupBlock } from '@/lib/auth/signupBlockLogger';
import { getCaptchaToken } from '@/lib/auth/turnstile';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  // HYDRATION FIX: Start with false to match SSR output exactly
  const [isLoading, setIsLoading] = useState(false);
  // 🛡️ FIX: useRef instead of useState to prevent effect self-cancellation
  // Setting state inside the effect's async callback triggered a re-render,
  // which ran cleanup (cancelled=true) before authResolved could be set.
  const hasInitializedRef = useRef(false);
  const [authResolved, setAuthResolved] = useState(false);
  const [hasSession, setHasSession] = useState(false);
  const [requiresMfa, setRequiresMfa] = useState(false);
  const [requiresConsent, setRequiresConsent] = useState(false);
  const [requiresDealerTerms, setRequiresDealerTerms] = useState(false);
  const isAuthenticated = !!user && !requiresMfa && !requiresConsent && !requiresDealerTerms;

  // Ref to store raw session user for decoupled enrichment
  const pendingSessionUserRef = useRef<{ id: string; email?: string | null; user_metadata?: any; email_confirmed_at?: string | null } | null>(null);
  // Track whether we need to enrich (incremented to trigger useEffect)
  const [enrichTrigger, setEnrichTrigger] = useState(0);
  // 🛡️ FIX: Skip listener enrichment when signIn/signUp already handle it
  const skipListenerEnrichmentRef = useRef(false);

  // Reusable function to load user from a Supabase session
  // ⚠️ MUST be called OUTSIDE onAuthStateChange to avoid GoTrue deadlock
  const loadUserFromSession = async (sessionUser: { id: string; email?: string | null; user_metadata?: any; email_confirmed_at?: string | null }, skipRoleCreation = false) => {
    try {
      // 🛡️ SERVER-AUTHORITATIVE: Fetch fresh user from server for emailConfirmedAt
      // Session claims can be stale after post-signup-verify resets email_confirmed_at
      // Fire-and-forget: role creation is idempotent, no need to block enrichment
      if (!skipRoleCreation) {
        roleService.createUserRole(sessionUser.id).catch(e => {
          debugError('auth', 'Role creation failed (non-blocking):', e, 'AuthProvider');
        });
      }

      // 🛡️ Per-query timeout (5s each) — prevents one slow query from blocking the entire enrichment
      const withTimeout = <T,>(p: Promise<T>, ms: number, fallback: T): Promise<T> =>
        Promise.race([
          p,
          new Promise<T>(resolve => setTimeout(() => {
            debugError('auth', `Query timed out after ${ms}ms (using fallback)`, undefined, 'AuthProvider');
            resolve(fallback);
          }, ms))
        ]);

      const [userRole, userProfile, verificationResult] = await Promise.all([
        withTimeout(roleService.fetchUserRole(sessionUser.id), 5000, null as any),
        withTimeout(getProfile(sessionUser.id), 5000, null),
        withTimeout(
          (async () => {
            try {
              return await supabase.from('email_verifications')
                .select('verified')
                .eq('user_id', sessionUser.id)
                .maybeSingle();
            } catch (e) {
              debugError('auth', 'email_verifications query failed:', e, 'AuthProvider');
              return { data: null, error: e };
            }
          })(),
          5000,
          { data: null, error: null } as any
        ),
      ]);

      // Autoritativo solo quando la riga esiste esplicitamente.
      // Se la query fallisce / RLS transitorio / timeout / row assente → fallback al claim JWT
      // (email_confirmed_at è emesso da GoTrue, firmato, non manipolabile lato client).
      const row = verificationResult?.data as { verified?: boolean } | null | undefined;
      const queryFailed = !!verificationResult?.error || row === null || row === undefined;
      if (verificationResult?.error) {
        console.warn('[AuthProvider] email_verifications query error:', verificationResult.error.message);
      }
      if (queryFailed) {
        debugError('auth', 'email_verifications unavailable, using JWT email_confirmed_at fallback', undefined, 'AuthProvider');
      }
      const emailVerified = row
        ? row.verified === true
        : !!sessionUser.email_confirmed_at;
      const emailConfirmedAt = emailVerified ? (sessionUser.email_confirmed_at || new Date().toISOString()) : null;

      const authUser: AuthUser = {
        id: sessionUser.id,
        email: sessionUser.email || '',
        role: userRole || 'USER',
        name: userProfile?.fullName || sessionUser.user_metadata?.name,
        dealer_activation_status: sessionUser.user_metadata?.dealer_activation_status || 'NONE',
        emailConfirmedAt,
        profile: userProfile ? {
          fullName: userProfile.fullName,
          avatarUrl: userProfile.avatarUrl,
          phone: userProfile.phone,
          address: userProfile.address
        } : undefined,
        settings: {
          twoFactorEnabled: false, // Deferred: loaded lazily on MFA settings page
          theme: sessionUser.user_metadata?.theme || 'light',
          notifications: sessionUser.user_metadata?.notifications || false
        }
      };
      return authUser;
    } catch (error) {
      debugError('auth', 'Error loading user from session:', error, 'AuthProvider');
      return null;
    }
  };

  // Helper: wrap loadUserFromSession with a timeout to prevent infinite spinner
  const loadUserWithTimeout = async (sessionUser: { id: string; email?: string | null; user_metadata?: any; email_confirmed_at?: string | null }, skipRoleCreation = false, timeoutMs = 12000): Promise<AuthUser | null> => {
    return Promise.race([
      loadUserFromSession(sessionUser, skipRoleCreation),
      new Promise<null>(resolve => {
        setTimeout(() => {
          debugError('auth', `loadUserFromSession timed out after ${timeoutMs}ms`, undefined, 'AuthProvider');
          resolve(null);
        }, timeoutMs);
      })
    ]);
  };

  // 🛡️ DECOUPLED ENRICHMENT: Runs outside onAuthStateChange to avoid GoTrue deadlock
  useEffect(() => {
    if (enrichTrigger === 0) return; // Skip initial mount
    const sessionUser = pendingSessionUserRef.current;
    if (!sessionUser) return;

    let cancelled = false;
    debugLog('auth', 'Decoupled enrichment starting for user', undefined, 'AuthProvider');

    loadUserWithTimeout(sessionUser, true).then(authUser => {
      if (cancelled) return;
      if (authUser) {
        setUser(authUser);
        debugLog('auth', 'Decoupled enrichment complete', undefined, 'AuthProvider');
      } else {
        // 🛡️ FALLBACK: Enrichment timed out — use basic session data to avoid limbo state
        // (hasSession=true but user=null). User will get enriched data on next refresh.
        const fallbackUser: AuthUser = {
          id: sessionUser.id,
          email: sessionUser.email || '',
          role: sessionUser.user_metadata?.role || 'USER',
          name: sessionUser.user_metadata?.name,
          dealer_activation_status: sessionUser.user_metadata?.dealer_activation_status || 'NONE',
          emailConfirmedAt: sessionUser.email_confirmed_at || null,
        };
        setUser(fallbackUser);
        debugError('auth', 'Enrichment timed out, using fallback session user to prevent limbo state', undefined, 'AuthProvider');
      }
    });

    return () => { cancelled = true; };
  }, [enrichTrigger]);

  // Initialize auth state AFTER hydration completes (not during)
  // 🛡️ FIX: Wait for HydrationManager signal before any setState
  useEffect(() => {
    // Guard: only run once via ref (no state change = no re-render = no self-cancellation)
    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;
    
    let cancelled = false;
    let unsubscribe: (() => void) | null = null;

    // 🛡️ Global watchdog: force loading=false after 8s no matter what
    const watchdog = setTimeout(() => {
      if (!cancelled) {
        debugError('auth', 'Auth init watchdog triggered (8s timeout)', undefined, 'AuthProvider');
        setIsLoading(false);
        setAuthResolved(true);
        // hasInitializedRef already set above
      }
    }, 8000);

    const runInit = async () => {
      // 🛡️ Wait for hydration to be fully complete (prevents React #421)
      await waitForHydrationComplete();
      
      if (cancelled) return;
      
      // 🛡️ Additional defer to next tick for Suspense settling
      setTimeout(async () => {
        if (cancelled) return;
        
        // Init guard already set via ref
        debugLog('auth', 'Auth initialization starting (post-hydration)', undefined, 'AuthProvider');

        // --- Initial session check with timeout ---
        setIsLoading(true);
        
        // 🛡️ FIX DEADLOCK: Listener is now SYNCHRONOUS — no async Supabase calls inside.
        // User enrichment is triggered via ref + state, handled by decoupled useEffect.
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
          if (cancelled) return;
          debugLog('auth', `Auth state changed: ${event}`, undefined, 'AuthProvider');

          if (event === 'SIGNED_OUT') {
            pendingSessionUserRef.current = null;
            setUser(null);
            setHasSession(false);
            setRequiresMfa(false);
            setRequiresConsent(false);
            setRequiresDealerTerms(false);
            useChatStore.getState().clearMessages();
            debugLog('auth', 'User signed out, state cleared', undefined, 'AuthProvider');
          } else if ((event === 'INITIAL_SESSION' || event === 'TOKEN_REFRESHED' || event === 'SIGNED_IN') && session) {
            setHasSession(true);
            // Clear pending email confirmation state — user is authenticated, email is confirmed
            safeLocalStorage.removeItem('gildy_pending_email');
            safeLocalStorage.removeItem('gildy_pending_email_at');
            // 🛡️ Skip enrichment if signIn/signUp is already handling it
            if (skipListenerEnrichmentRef.current) {
              debugLog('auth', `${event} detected, skipping enrichment (handled by signIn/signUp)`, undefined, 'AuthProvider');
              return;
            }
            debugLog('auth', `${event} detected, scheduling decoupled enrichment`, undefined, 'AuthProvider');
            // Store session user and trigger enrichment outside listener
            pendingSessionUserRef.current = session.user;
            setEnrichTrigger(prev => prev + 1);
          }
        });
        unsubscribe = () => subscription.unsubscribe();
        
        // --- Initial session check (AFTER listener) ---
        // No timeout race needed — listener is now sync, no deadlock risk.
        try {
          const sessionResult = await supabase.auth.getSession();
          
          if (sessionResult.error) {
            debugError('auth', 'Error getting auth session:', sessionResult.error, 'AuthProvider');
            if (!cancelled) { setUser(null); setHasSession(false); }
          } else if (sessionResult.data.session) {
            if (!cancelled) setHasSession(true);
            // User loading is handled by INITIAL_SESSION event above.
            // No second loadUserWithTimeout here — that caused the race condition.
            debugLog('auth', 'getSession confirmed session exists, user loading delegated to listener', undefined, 'AuthProvider');
          } else {
            if (!cancelled) { setUser(null); setHasSession(false); }
          }
        } catch (error) {
          debugError('auth', 'Auth initialization error:', error, 'AuthProvider');
          if (!cancelled) setUser(null);
        } finally {
          if (!cancelled) {
            setIsLoading(false);
            setAuthResolved(true);
          }
          clearTimeout(watchdog);
          debugLog('auth', 'Auth initialization completed', undefined, 'AuthProvider');
        }
      }, 0);
    };
    
    runInit();

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, []); // Empty deps: runs once on mount, ref prevents re-execution

  // Sign in function with improved error handling and MFA support
  const signIn = async (email: string, password: string): Promise<{ requiresMfa?: boolean; emailNotConfirmed?: boolean }> => {
    setIsLoading(true);
    skipListenerEnrichmentRef.current = true; // Prevent double enrichment from listener
    debugLog('auth', 'Attempting sign in for:', email, 'AuthContext');
    
    try {
      // Captcha nativo Supabase Auth (no-op se Turnstile non è configurato)
      const captchaToken = await getCaptchaToken('signin');
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
        ...(captchaToken ? { options: { captchaToken } } : {})
      });


      if (error) {
        debugError('auth', 'Sign in error:', error, 'AuthContext');
        
        // Email non confermata: permettiamo il login, il banner e i guard gestiscono le restrizioni
        
        // Gestione credenziali non valide
        if (error.message?.includes('Invalid login credentials')) {
          toast.error('Credenziali non valide', {
            description: 'Email o password non corretti.',
          });
          setIsLoading(false);
          return {};
        }
        
        // Fallback generico
        toast.error('Login fallito', {
          description: error.message || 'Si è verificato un errore durante il login',
        });
        setIsLoading(false);
        return {};
      }

      if (data.user) {
        debugLog('auth', 'Sign in successful for:', data.user.id, 'AuthContext');
        
        // Query custom email_verifications table (autoritativo solo se row esiste esplicitamente)
        const { data: verificationData, error: verificationError } = await supabase
          .from('email_verifications')
          .select('verified')
          .eq('user_id', data.user.id)
          .maybeSingle();
        if (verificationError) {
          debugError('auth', 'email_verifications query error on signIn, using JWT fallback', verificationError, 'AuthContext');
        }
        const serverEmailVerified = verificationData
          ? verificationData.verified === true
          : !!data.user.email_confirmed_at;
        const serverEmailConfirmedAt = serverEmailVerified ? (data.user.email_confirmed_at || new Date().toISOString()) : null;
        
        // Check if MFA is required
        const { data: aalData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        
        if (aalData?.currentLevel === 'aal1' && aalData?.nextLevel === 'aal2') {
          // User has MFA enabled but hasn't completed the challenge
          debugLog('auth', 'MFA required for user', undefined, 'AuthContext');
          setRequiresMfa(true);
          
          // Store partial user data for after MFA
          const [userRole, userProfile] = await Promise.all([
            roleService.fetchUserRole(data.user.id),
            getProfile(data.user.id)
          ]);
          const authUser: AuthUser = {
            id: data.user.id,
            email: data.user.email || '',
            role: userRole || 'USER',
            name: userProfile?.fullName || data.user.user_metadata?.name,
            dealer_activation_status: data.user.user_metadata?.dealer_activation_status || 'NONE',
            emailConfirmedAt: serverEmailConfirmedAt,
            profile: userProfile ? {
              fullName: userProfile.fullName,
              avatarUrl: userProfile.avatarUrl
            } : undefined,
            settings: {
              twoFactorEnabled: true,
              theme: data.user.user_metadata?.theme || 'light',
              notifications: data.user.user_metadata?.notifications || false
            }
          };
          setUser(authUser);
          setHasSession(true);
          setIsLoading(false);
          return { requiresMfa: true };
        }
        
        // No MFA required, complete login — parallelize calls (P2 FIX)
        const [_roleCreated, userRole, userProfile] = await Promise.all([
          roleService.createUserRole(data.user.id).catch(e => {
            debugError('auth', 'Role creation failed (non-blocking):', e, 'AuthContext');
          }),
          roleService.fetchUserRole(data.user.id),
          getProfile(data.user.id)
        ]);
        debugLog('auth', 'User role after login:', userRole, 'AuthContext');
        
        const authUser: AuthUser = {
          id: data.user.id,
          email: data.user.email || '',
          role: userRole || 'USER',
          name: userProfile?.fullName || data.user.user_metadata?.name,
          dealer_activation_status: data.user.user_metadata?.dealer_activation_status || 'NONE',
          emailConfirmedAt: serverEmailConfirmedAt,
          profile: userProfile ? {
            fullName: userProfile.fullName,
            avatarUrl: userProfile.avatarUrl,
            phone: userProfile.phone,
            address: userProfile.address
          } : undefined,
          settings: {
            twoFactorEnabled: false, // No MFA factors enrolled (AAL1 path)
            theme: data.user.user_metadata?.theme || 'light',
            notifications: data.user.user_metadata?.notifications || false
          }
        };
        
        setUser(authUser);
        setHasSession(true);
        setRequiresMfa(false);
        
        // Check if user has accepted privacy policy (GDPR)
        const hasConsent = await checkUserConsent(data.user.id);
        if (!hasConsent) {
          // Fallback: check user_metadata for consent given during registration
          const metadataPrivacy = data.user.user_metadata?.privacy_accepted === true;
          const metadataMarketing = data.user.user_metadata?.marketing_consent === true;
          
          if (metadataPrivacy) {
            debugLog('auth', 'Consent found in user_metadata, syncing to DB', undefined, 'AuthContext');
            await savePostConfirmationConsents(data.user.id, metadataPrivacy, metadataMarketing);
          } else {
            debugLog('auth', 'User requires consent acceptance', undefined, 'AuthContext');
            setRequiresConsent(true);
            setIsLoading(false);
            return {};
          }
        }
        // Check if dealer has accepted dealer terms
        const normalizedRole = (userRole || '').toUpperCase();
        if (normalizedRole === 'DEALER') {
          const hasDealerTerms = await checkDealerTermsConsent(data.user.id);
          if (!hasDealerTerms) {
            debugLog('auth', 'Dealer requires terms acceptance', undefined, 'AuthContext');
            setRequiresDealerTerms(true);
            setIsLoading(false);
            return {};
          }
        }
        
        toast.success('Login effettuato con successo');
      }
    } catch (error: any) {
      debugError('auth', 'Login error:', error, 'AuthContext');
      toast.error(error.message || 'Login fallito');
    } finally {
      setIsLoading(false);
      skipListenerEnrichmentRef.current = false; // Re-enable listener enrichment
    }
    return {};
  };

  // Complete MFA challenge
  const completeMfaChallenge = async () => {
    setRequiresMfa(false);
    
    // After MFA, also check consent
    if (user) {
      const hasConsent = await checkUserConsent(user.id);
      if (!hasConsent) {
        // Fallback: check user_metadata for consent given during registration
        const { data: { user: currentUser } } = await supabase.auth.getUser();
        const metadataPrivacy = currentUser?.user_metadata?.privacy_accepted === true;
        const metadataMarketing = currentUser?.user_metadata?.marketing_consent === true;
        
        if (metadataPrivacy) {
          debugLog('auth', 'Consent found in user_metadata after MFA, syncing to DB', undefined, 'AuthContext');
          await savePostConfirmationConsents(user.id, metadataPrivacy, metadataMarketing);
        } else {
          debugLog('auth', 'User requires consent after MFA', undefined, 'AuthContext');
          setRequiresConsent(true);
          return;
        }
      }
      
      // Check dealer terms after consent
      const normalizedRole = (user.role || '').toUpperCase();
      if (normalizedRole === 'DEALER') {
        const hasDealerTerms = await checkDealerTermsConsent(user.id);
        if (!hasDealerTerms) {
          debugLog('auth', 'Dealer requires terms after MFA', undefined, 'AuthContext');
          setRequiresDealerTerms(true);
          return;
        }
      }
    }
    
    toast.success('Login effettuato con successo');
  };

  // Complete consent challenge - save consent and complete login
  const completeConsentChallenge = async (marketingConsent: boolean) => {
    if (!user) return;
    
    debugLog('auth', 'Completing consent challenge', undefined, 'AuthContext');
    
    const success = await saveUserConsent(user.id, marketingConsent);
    
    if (success) {
      setRequiresConsent(false);
      
      // After consent, check dealer terms if dealer
      const normalizedRole = (user.role || '').toUpperCase();
      if (normalizedRole === 'DEALER') {
        const hasDealerTerms = await checkDealerTermsConsent(user.id);
        if (!hasDealerTerms) {
          debugLog('auth', 'Dealer requires terms after consent', undefined, 'AuthContext');
          setRequiresDealerTerms(true);
          return;
        }
      }
      
      toast.success('Login effettuato con successo');
    } else {
      toast.error('Errore durante il salvataggio del consenso. Riprova.');
    }
  };

  // Complete dealer terms challenge
  const completeDealerTermsChallenge = async () => {
    if (!user) return;
    
    debugLog('auth', 'Completing dealer terms challenge', undefined, 'AuthContext');
    
    const success = await saveDealerTermsConsent(user.id);
    
    if (success) {
      setRequiresDealerTerms(false);
      toast.success('Login effettuato con successo');
    } else {
      toast.error('Errore durante il salvataggio del consenso. Riprova.');
    }
  };

  // Cancel MFA and sign out
  const cancelMfa = async () => {
    await supabase.auth.signOut();
    
    // Clear chat history on logout
    useChatStore.getState().clearMessages();
    debugLog('auth', 'Chat history cleared on MFA cancel/logout', undefined, 'AuthProvider');
    
    setUser(null);
    setRequiresMfa(false);
  };

  // Sign up function with improved error handling and metadata support
  // GDPR: Now accepts privacy and marketing consent flags to store in user_metadata
  const signUp = async (
    email: string, 
    password: string, 
    firstName?: string, 
    lastName?: string, 
    privacyAccepted?: boolean, 
    marketingConsent?: boolean
  ) => {
    setIsLoading(true);
    skipListenerEnrichmentRef.current = true; // Prevent double enrichment from listener
    const fullName = [firstName, lastName].filter(Boolean).join(' ') || '';
    debugLog('auth', `Attempting sign up for: ${email} with firstName: ${firstName}, lastName: ${lastName}, privacy: ${privacyAccepted}, marketing: ${marketingConsent}`, undefined, 'AuthContext');
    
    try {
      // Difesa in profondità: blocca email non recapitabili/temporanee/bot
      const guard = checkSignupEmail(email, await getEmailGuardOptionsCached());
      if (!guard.allowed) {
        debugError('auth', `Sign up blocked by emailGuard: ${guard.reason}`, undefined, 'AuthContext');
        logSignupBlock({
          flow: 'user_signup',
          reason: guard.reason || 'other',
          detail: guard.message,
          email,
        });
        toast.error(guard.message || 'Indirizzo email non utilizzabile');
        return undefined;
      }

      // Captcha nativo Supabase Auth (attivo solo se VITE_TURNSTILE_SITE_KEY è configurata)
      const captchaToken = await getCaptchaToken('signup');

      // Include user metadata in the signup request
      // GDPR Art. 7: Store consents in metadata for post-confirmation registration
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name: fullName,
            first_name: firstName || '',
            last_name: lastName || '',
            role: 'USER',
            created_at: new Date().toISOString(),
            // GDPR: Store consents for retrieval after email confirmation
            privacy_accepted: privacyAccepted ?? false,
            marketing_consent: marketingConsent ?? false,
            consent_timestamp: new Date().toISOString(),
          },
          emailRedirectTo: (typeof window !== 'undefined' ? window.location.origin : 'https://www.gildy.it') + '/auth/verify',
          ...(captchaToken ? { captchaToken } : {})
        }
      });


      if (error) {
        debugError('auth', 'Sign up error:', error, 'AuthContext');
        toast.error(error.message || 'Registrazione fallita');
        return undefined;
      }

      // CHECK: Email già in uso (Supabase restituisce user con identities vuote)
      if (data.user && data.user.identities && data.user.identities.length === 0) {
        debugLog('auth', 'Email già registrata (identities vuote)', undefined, 'AuthContext');
        toast.error('Email già in uso. Prova con un\'altra email.');
        return undefined; // STOP - non proseguire con creazione ruoli
      }

      // Verifica che l'utente sia stato realmente creato
      const userReallyCreated = data.session || (data.user?.identities && data.user.identities.length > 0);

      if (userReallyCreated && data.user) {
        debugLog('auth', 'Sign up successful for:', data.user.id, 'AuthContext');

        // Soft-gate: l'utente resta loggato, ma forziamo il reset di email_confirmed_at
        // per attivare banner e guard fino alla verifica email
        try {
          const { error: resetError } = await supabase.functions.invoke('post-signup-verify', {
            body: { userId: data.user.id, email: data.user.email },
          });
          if (resetError) {
            debugError('auth', 'Failed to reset email verification:', resetError, 'AuthContext');
            // Fallback: create unverified record directly if Edge Function fails
            try {
              await supabase.from('email_verifications')
                .upsert({ user_id: data.user!.id, verified: false, verified_at: null }, { onConflict: 'user_id' });
            } catch { /* ignore fallback errors */ }
          } else {
            debugLog('auth', 'Email verification reset successfully, confirmation email sent', undefined, 'AuthContext');
            // 🛡️ Refresh session to sync token claims with server-side reset
            // This prevents TOKEN_REFRESHED from reintroducing stale email_confirmed_at
            await supabase.auth.refreshSession().catch(e => {
              debugError('auth', 'refreshSession after post-signup-verify failed (non-blocking):', e, 'AuthContext');
            });
          }
        } catch (resetErr) {
          debugError('auth', 'Error calling post-signup-verify:', resetErr, 'AuthContext');
        }

        // Create default role for new user with retry mechanism
        if (data.session) {
          try {
            for (let attempt = 0; attempt < 3; attempt++) {
              try {
                await roleService.createUserRole(data.user.id);
                const userRole = await roleService.fetchUserRole(data.user.id);
                debugLog('auth', 'New user role created:', userRole, 'AuthContext');
                break;
              } catch (roleError) {
                debugError('auth', `Role creation attempt ${attempt + 1} failed:`, roleError, 'AuthContext');
                if (attempt === 2) throw roleError;
                await new Promise(resolve => setTimeout(resolve, 500));
              }
            }
          } catch (roleError) {
            debugError('auth', 'All role creation attempts failed:', roleError, 'AuthContext');
          }
        }

        // Soft-gate: utente loggato con emailConfirmedAt null → banner + guard attivi
        const authUser: AuthUser = {
          id: data.user.id,
          email: data.user.email || '',
          role: 'USER',
          name: fullName || data.user.user_metadata?.name,
          emailConfirmedAt: null, // Forzato a null per attivare soft-gate
          dealer_activation_status: 'NONE',
          settings: {
            twoFactorEnabled: false,
            theme: 'light',
            notifications: false
          }
        };

        setUser(authUser);
        setHasSession(true);
        toast.success('Registrazione completata! Controlla la tua email per verificare l\'account.');
        return { requiresEmailConfirmation: false };
      }
    } catch (error: any) {
      debugError('auth', 'Registration error:', error, 'AuthContext');
      toast.error(error.message || 'Registrazione fallita');
    } finally {
      setIsLoading(false);
      skipListenerEnrichmentRef.current = false; // Re-enable listener enrichment
    }
  };

  // Sign out function with improved error handling
  const signOut = async () => {
    setIsLoading(true);
    debugLog('auth', 'Attempting sign out', undefined, 'AuthContext');
    
    try {
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        debugError('auth', 'Sign out error:', error, 'AuthContext');
        toast.error(error.message || 'Logout fallito');
        // Still clear chat even on error for privacy
      } else {
        debugLog('auth', 'Sign out successful', undefined, 'AuthContext');
        toast.success('Logout effettuato con successo');
      }
      
      setUser(null);
    } catch (error: any) {
      debugError('auth', 'Logout error:', error, 'AuthContext');
      toast.error(error.message || 'Logout fallito');
      
      // Force user state to null even if the API call fails
      setUser(null);
    } finally {
      // Always clear chat history on logout for privacy
      useChatStore.getState().clearMessages();
      debugLog('auth', 'Chat history cleared on logout', undefined, 'AuthProvider');
      
      setIsLoading(false);
    }
  };

  // Role check function
  const hasRole = (role: string) => {
    if (!user) return false;
    return checkRoleAccess(user.role, role);
  };

  return (
    <AuthContext.Provider value={{
      user,
      isLoading,
      isAuthenticated,
      authResolved,
      hasSession,
      requiresMfa,
      requiresConsent,
      requiresDealerTerms,
      signIn,
      signUp,
      signOut,
      hasRole,
      completeMfaChallenge,
      cancelMfa,
      completeConsentChallenge,
      completeDealerTermsChallenge
    }}>
      {children}
    </AuthContext.Provider>
  );
}
